import './MyAccount.scss';

import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Country, Toast } from '@travelwin/core';
import classNames from 'classnames';
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useOutlet } from 'react-router';

import Footer from '../../components/Footer';
import HelmetWrapper from '../../components/HelmetWrapper';
import { Layout } from '../../components/Layout';
import PageTitle from '../../components/PageTitle';
import { useConsent } from '../../context/Consent';
import { useUser } from '../../context/User';
import { FeaturedCountry } from '../../services/resources/ResourceService';
import { LoggedOutUserView } from '../Orders/components';
import AccountMenu from './components/AccountMenu';
import LanguageAndCurrencySwitch from './components/LanguageAndCurrencySwitch';
import AccountInformation from './subpages/AccountInformation';

const className = 'p-MyAccount';

type Props = {
  featuredCountries: FeaturedCountry[];
  countries: Country[];
  returnPath?: string;
  openSignupModal?: boolean;
};

const MyAccount: FC<Props> = ({
  featuredCountries,
  countries,
  returnPath,
  openSignupModal,
}) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useUser();
  const isNative = Capacitor.isNativePlatform();
  const hasOutlet = useOutlet();
  const { enabled: consentEnabled, renewCookiebot } = useConsent();
  const location = useLocation();
  const userDeleted = location.state?.userDeleted;

  const [title, setTitle] = useState<ReactNode>(<MyAccountTitle />);
  const [mobileAppVersion, setMobileAppVersion] = useState('');

  const getMobileAppInfo = useCallback(async () => {
    const appInfo = await CapacitorApp.getInfo();
    setMobileAppVersion(`v${appInfo.version} (${appInfo.build})`);
  }, []);

  useEffect(() => {
    if (isNative) {
      getMobileAppInfo();
    }
  }, [getMobileAppInfo]);

  const renderLogin = () => (
    <LoggedOutUserView
      cardTitle={t('my_account.log_in', 'Log In to edit your account')}
      cardText={t(
        'my_account.log_in_to_manage',
        'Log in or sign up below to manage your account details.',
      )}
      buttonText={t('login.login_or_sign_up', 'Log In or Sign Up')}
      returnPath={returnPath ?? '/'}
      showLogin={openSignupModal}
    />
  );

  const renderConsentChange = () => {
    if (!consentEnabled) {
      return null;
    }

    return (
      <button
        type="button"
        className={`${className}__change-consent`}
        onClick={renewCookiebot}
      >
        {t('my_account.change_consent', 'Manage Cookie preferences')}
      </button>
    );
  };

  const renderDeleteSuccess = () => {
    return (
      <div className={`${className}__message_wrapper`}>
        <Toast>
          {t(
            'my_account.account_delete_success',
            'Your account has been deleted.',
          )}
        </Toast>
      </div>
    );
  };

  const renderMobileVersion = () => {
    return (
      <span className={`${className}__app_version`}>{mobileAppVersion}</span>
    );
  };

  const renderLayout = (isDesktop: boolean = false) => {
    return (
      <>
        <HelmetWrapper title={t('browser.myAccountTitle', 'My Account')} />
        <LanguageAndCurrencySwitch />
        {isAuthenticated ? (
          <div className={className}>
            <div
              className={classNames(`${className}__menu`, {
                [`${className}__menu-outlet`]: hasOutlet,
              })}
            >
              <AccountMenu />
            </div>

            {isDesktop && !hasOutlet && (
              <div className={`${className}__outlet`}>
                <AccountInformation />
              </div>
            )}

            {hasOutlet && (
              <div className={`${className}__outlet`}>
                <Outlet context={{ setTitle }} />
              </div>
            )}
          </div>
        ) : (
          <div className={`${className}__not-authenticated`}>
            {userDeleted && renderDeleteSuccess()}
            {renderLogin()}
            {mobileAppVersion && renderMobileVersion()}
            {renderConsentChange()}
          </div>
        )}
        {!isNative && (
          <Footer featuredCountries={featuredCountries} countries={countries} />
        )}
      </>
    );
  };

  return (
    <>
      <div
        data-testid="mobile-layout"
        className={`${className}__mobile-layout`}
      >
        <Layout header={title}>{renderLayout()}</Layout>
      </div>
      <div
        data-testid="desktop-layout"
        className={`${className}__desktop-layout`}
      >
        <Layout header={<MyAccountTitle />}>{renderLayout(true)}</Layout>
      </div>
    </>
  );
};

export const MyAccountTitle = () => {
  const { t } = useTranslation();
  return (
    <PageTitle
      data-testid="title-my-account"
      title={t('my_account.header_text', 'My Account')}
    />
  );
};

export type OutletContextType = {
  setTitle: (title: ReactNode) => void;
};

export default MyAccount;
